import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  LoginControllerService,
  LoginRequest,
  LoginResponse,
  User2FAControllerService,
  UserChangePasswordRequest,
  UserControllerService,
} from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { requiresPasswordChange } from '@shared/store/features/current-user.feature';
import { SharedStore } from '@shared/store/shared.signal-store';
import { GoogleIdentityService } from '@stillmandigital/common';
import { AuthService } from 'app/auth/services/auth.service';
import { pipe, switchMap } from 'rxjs';
import { readEmailFromCredential } from './google-credential';
import { GoogleSsoStep, mapGoogleSsoError } from './google-sso-errors';

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState({
    twoFASecretKey: undefined as string | undefined,
    twoFAQrCodeLink: undefined as string | undefined,
    error: null as string | null,
    ssoStep: 'credentials' as GoogleSsoStep,
    ssoEmail: null as string | null,
    ssoPending: false,
    _ssoCredential: null as string | null,
  }),
  withProps(() => ({
    _sharedStore: inject(SharedStore),
    _usersService: inject(UserControllerService),
    _loginService: inject(LoginControllerService),
    _googleIdentity: inject(GoogleIdentityService),
    _user2FAService: inject(User2FAControllerService),
    _authService: inject(AuthService),
    _notificationService: inject(NotificationService),
    _router: inject(Router),
  })),
  withComputed(store => {
    const isTwoFAEnabled = computed(() => {
      const user = store._sharedStore.currentUser();
      return user?.enabled2FA ?? false;
    });
    const twoFALink = computed(() => {
      const user = store._sharedStore.currentUser();
      const key = store.twoFASecretKey();
      return `otpauth://totp/${user?.login}?secret=${key}&algorithm=SHA1&digits=6&period=30&issuer=Stillman%20Digital`;
    });

    return { twoFALink, isTwoFAEnabled };
  }),
  withMethods(store => {
    const onLoginSuccess = ({ token }: LoginResponse) => {
      patchState(store, { ssoPending: false });
      store._authService.setToken(token || '');
      store._authService.navigateToReturnUrl();
    };

    const login = rxMethod<LoginRequest>(
      pipe(
        switchMap(request =>
          store._loginService.login({ loginRequest: request }).pipe(
            tapResponse({
              next: onLoginSuccess,
              error: (error: HttpErrorResponse) =>
                patchState(store, {
                  error: error.error?.message ?? 'Something went wrong. Please try again.',
                }),
            }),
          ),
        ),
      ),
    );

    const loginWithGoogle = rxMethod<string | undefined>(
      pipe(
        switchMap(twoFACode =>
          store._loginService
            .loginWithGoogle({
              googleLoginRequest: {
                credential: store._ssoCredential() ?? '',
                twoFACode,
              },
            })
            .pipe(
              tapResponse({
                next: onLoginSuccess,
                error: (error: HttpErrorResponse) => {
                  const { nextStep, message, keepCredential } = mapGoogleSsoError(error);

                  patchState(store, {
                    ssoStep: nextStep,
                    error: message,
                    ssoPending: false,
                    _ssoCredential: keepCredential ? store._ssoCredential() : null,
                    ssoEmail: keepCredential ? store.ssoEmail() : null,
                  });
                },
              }),
            ),
        ),
      ),
    );

    const startGoogleLogin = (credential: string) => {
      patchState(store, {
        error: null,
        ssoPending: true,
        _ssoCredential: credential,
        ssoEmail: readEmailFromCredential(credential),
      });
      loginWithGoogle(undefined);
    };

    const submitGoogleTwoFactor = (twoFACode: string) => {
      patchState(store, { error: null, ssoPending: true });
      loginWithGoogle(twoFACode);
    };

    const cancelGoogleLogin = () => {
      store._googleIdentity.disableAutoSelect();
      patchState(store, {
        ssoStep: 'credentials',
        ssoEmail: null,
        ssoPending: false,
        error: null,
        _ssoCredential: null,
      });
    };

    const logout = rxMethod<void>(
      pipe(
        switchMap(() =>
          store._loginService.logout({ logoutRequest: { sessionToken: store._authService.getToken() } }).pipe(
            tapResponse({
              next: () => {
                store._googleIdentity.disableAutoSelect();
                store._authService.logOut();
              },
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification('Logout failed', error),
            }),
          ),
        ),
      ),
    );

    const changePassword = rxMethod<UserChangePasswordRequest>(
      pipe(
        switchMap(request =>
          store._usersService.changePassword({ userChangePasswordRequest: request }).pipe(
            tapResponse({
              next: () => {
                store._notificationService.showSuccessNotification('The password has been changed successfully');
                store._authService.logOut();
              },
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification("The password couldn't be changed", error),
            }),
          ),
        ),
      ),
    );

    const enable2FA = rxMethod<string>(
      pipe(
        switchMap(twoFACode =>
          store._user2FAService.enable2FASecurity({ enableDisable2FASecurityRequest: { twoFACode } }).pipe(
            tapResponse({
              next: () => {
                store._notificationService.showSuccessNotification('The 2fa has been enabled successfully');
                store._authService.logOut();
              },
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification("The 2fa couldn't be enabled", error),
            }),
          ),
        ),
      ),
    );

    const disable2FA = rxMethod<string>(
      pipe(
        switchMap(twoFACode =>
          store._user2FAService.disable2FASecurity({ enableDisable2FASecurityRequest: { twoFACode } }).pipe(
            tapResponse({
              next: () => {
                store._notificationService.showSuccessNotification('The 2fa has been disabled successfully');
                store._authService.logOut();
              },
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification("The 2fa couldn't be disabled", error),
            }),
          ),
        ),
      ),
    );

    const load2FALink = rxMethod<void>(
      pipe(
        switchMap(() =>
          store._user2FAService.get2FASecurity().pipe(
            tapResponse({
              next: ({ secretKey }) =>
                patchState(store, {
                  twoFASecretKey: secretKey,
                }),
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification('Failed to load 2FA link', error),
            }),
          ),
        ),
      ),
    );

    const clearError = () => {
      patchState(store, { error: null });
    };

    return {
      login,
      startGoogleLogin,
      submitGoogleTwoFactor,
      cancelGoogleLogin,
      logout,
      changePassword,
      enable2FA,
      disable2FA,
      load2FALink,
      clearError,
    };
  }),
  withHooks(store => ({
    onInit() {
      effect(() => {
        const user = store._sharedStore.currentUser();
        if (user && !requiresPasswordChange(user) && !user.enabled2FA) {
          store._router.navigateByUrl('/auth-setup');
        }
      });
    },
  })),
);
